import { ChevronLeft } from '@mui/icons-material';
import { Box, Button, MenuItem, Select, SelectChangeEvent, Stack, Typography, useMediaQuery } from '@mui/material';
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { GetOrderResponse } from '../../types';
import houses from '../../../assets/houses.svg';
import { addressOffset, getCalStartDate, humanize, toTitleCase } from '../../utils';
import { useNavigateWithSearchParams, usePageTitle } from '../../logic';
import { LoadingScreen } from './';
import { useEffect, useState } from 'react';
import { uniq } from 'lodash';
import { ClosingDetailsHeader } from './ClosingDetailsSection';
import { useGetOrder } from '../../logic/useApiQuery';
import { useAppTheme } from '../../logic/useAppTheme';
import { ClosingDetailsDialog } from './dialogs/ClosingDetailsDialog';
import { isAdhocPortalOrder } from '../../utils/isAdhocPortalOrder';
import { ClosingHeaderActions } from './ClosingHeaderActions';

export const ADDRESS_CHARS_REGEX = /[a-zA-Z0-9]+/


export function RoleDetail({ globalOrderData }: {
  globalOrderData: GetOrderResponse | undefined;
}) {
  const { closingId } = useParams()
  let { Role, RoleOptions = [] } = globalOrderData || {}

  if (!Role) return null;

  function onChangeRole(e: SelectChangeEvent) {
    (window as Window).location = `/closings/${closingId}?role=${e.target.value}`
  }

  return (
    <Select
      size='medium'
      label='Role'
      sx={{
        textAlign: 'left',
        color: '#FFFFFF',
        height: '39px',
        borderRadius: '30px',
        width: '100%',
        mt: '5px',
        '.MuiSelect-select': { pl: 2, pr: 2 },
        '.MuiSelect-icon': { color: '#FFFFFF' },
        '.MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF', },

        '&:hover': {
          bgcolor: 'primary.light', color: 'white', border: 'none',
          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
        },
      }}
      // autoWidth={true}
      value={Role}
      defaultValue={Role}
      onChange={onChangeRole}
    >
      {uniq(RoleOptions).map((role) => {
        return <MenuItem key={role} value={role}>{toTitleCase(humanize(role))}</MenuItem>
      })}
    </Select>
  )
}


export function ClosingHeader({titleOverride, subtitleOverride}: {titleOverride?: string, subtitleOverride?: string}) {
  const { section, closingId } = useParams()
  const { search } = useLocation()
  const theme = useAppTheme() 
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const onlySection = isSmall && section
  const { data: globalOrderData, isLoading: globalOrderDataIsLoading} = useGetOrder(closingId ?? '', search ?? '')
  const [addToCalendarOpen, setAddToCalendarOpen] = useState(false)

  let [searchParams] = useSearchParams();
  const component = searchParams.get('component')
  const isSigningDetailsLink = component === 'signing-details'
  const isAddToCalLink = component === 'add-to-cal'


  const order = globalOrderData?.Order
  const hasParseableTime = order && !isAdhocPortalOrder(order) ? !!getCalStartDate(order) : false

  const openSigningDetails = isSigningDetailsLink
  const openAddToCal = hasParseableTime && isAddToCalLink

  const [closingDetailsExpanded, setClosingDetailsExpanded] = useState(openSigningDetails || openAddToCal);
  const [closingDetailsDialogOpen, setClosingDetailsDialogOpen] = useState(openSigningDetails);

  usePageTitle(titleOverride ?? globalOrderData?.Order?.PropertyAddress?.Street)

  useEffect(() => {
    if (openSigningDetails && !closingDetailsExpanded) {
      setClosingDetailsExpanded(true)
    }
    if (openSigningDetails && !closingDetailsDialogOpen) {
      setClosingDetailsDialogOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSigningDetails])

  useEffect(() => {
    if (openAddToCal && !addToCalendarOpen) {
      setAddToCalendarOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAddToCal])

  // TODO: any better way to handle lack of globalOrderData?
  if (!titleOverride && (globalOrderDataIsLoading || !globalOrderData || !order)) {
    return <LoadingScreen />
  }

  const propertyAddress = order?.PropertyAddress
  const fileNumber = order?.FileNumber

  const displayTitle = titleOverride ?? propertyAddress?.Street
  const displayTitleLine2 = titleOverride ? undefined : (propertyAddress ? `${propertyAddress.City}, ${propertyAddress.State} ${propertyAddress.Zip}` : undefined)
  const displaySubtitle = subtitleOverride ? subtitleOverride : `#${fileNumber}`

  const xPosAddressHash = titleOverride ? 0 : propertyAddress && addressOffset(propertyAddress)

  const role = searchParams.get('role')
  const showBackButton = role !== 'AdHoc' 


  const mobileSectionOnly = (
    <Box height='100%' p='0'>
      <Box sx={{
        borderBottomLeftRadius: '20px',
        display: 'flex', alignItems: { xs: 'initial', sm: 'center' }, padding: 2, bgcolor: 'primary.main',
        color: 'primary.contrastText', borderRadius: '0 0 0 20px',
        height: '92px',
      }}>
        { showBackButton && <BackButton /> }

        <Box pl={3}>
          <div style={{ fontSize: '1.13rem', lineHeight: '20px', fontWeight: 500 }}>
            <Typography sx={{
              fontSize: '18px',
              fontWeight: '500',
              lineHeight: '20px',
            }}>
              {displayTitle}
              {displayTitleLine2 && <><br/>{displayTitleLine2}</>}
            </Typography>
            {displaySubtitle ? <Typography fontSize="small" fontWeight={300}>{displaySubtitle}</Typography> : ''}
          </div>
        </Box>
      </Box>
      <Box sx={{ height: 'auto', backgroundColor: 'primary.main' }}>
        <Stack
          direction='column' spacing={2} height='100%'
          sx={{
            p: 2, borderLeft: 'none',
            backgroundColor: '#F3F3F3', borderTopRightRadius: '20px',
          }}
        >
          { <Outlet/> }
        </Stack>
      </Box>
    </Box>
  )

  // NOTE: this header is still using the old blend mode (see mixBlendMode elsewhere)
  // because it doesn't have text over the top to require more opacity in the background.
  // We could change it in the future if we want it to look more consistent. I think I
  // prefer the extra contrast in the background here.
  const mobile = (
    <Box height='100%' p='0'>
      <Box sx={{
        backgroundImage: `url(${houses})`,
        backgroundRepeat: 'repeat-x',
        backgroundPosition: `${xPosAddressHash || 0}% 100%`,
        borderBottomLeftRadius: '20px', flexDirection: 'column',
        display: 'flex', padding: 2, bgcolor: 'primary.main',
        color: 'primary.contrastText', borderRadius: '0 0 0 20px',
        backgroundBlendMode: 'luminosity',
      }}>
        <Box sx={{ display: 'flex' }}>
          { showBackButton && <BackButton /> }

          <Box
            sx={{ pl: 3, width: '100%' }}
          >
            <div style={{ fontSize: '1.13rem', lineHeight: '20px', fontWeight: 500 }}>
              <Typography sx={{
                fontSize: '18px',
                fontWeight: '500',
                lineHeight: '20px',
              }}>
                {displayTitle}
                {displayTitleLine2 && <><br/>{displayTitleLine2}</>}
              </Typography>
              {displaySubtitle ? <Typography fontSize="small" fontWeight={300}>{displaySubtitle}</Typography> : ''}            </div>
          </Box>

        </Box>

        { order && !isAdhocPortalOrder(order) ?
          <ClosingDetailsHeader
            setAddToCalendarOpen={setAddToCalendarOpen} addToCalendarOpen={addToCalendarOpen}
            order={order} closingDetailsExpanded={closingDetailsExpanded}
            setClosingDetailsExpanded={setClosingDetailsExpanded}
            hasParseableTime={hasParseableTime}
          />
          : null }

      </Box>
      <Box sx={{ height: 'auto', backgroundColor: 'primary.main' }}>
        <Stack
          direction='column' spacing={2} height='100%'
          sx={{
            p: 2, borderLeft: 'none',
            backgroundColor: '#F3F3F3', borderTopRightRadius: '20px',
          }}
        >
          { <Outlet/> }
        </Stack>
      </Box>
    </Box>
  )

  const desktop = ( 
    <>
      <Box height='100%' p='30px 0 0 20px'>
        <Box sx={{
          display: 'flex', alignItems: 'center', padding: 2, bgcolor: 'primary.main',
          color: 'primary.contrastText', borderRadius: { xs: '0 0 0 20px', sm: '20px 0 0 0'},
          height: '92px',
        }}>
          { showBackButton && <BackButton /> }

          <Box pl={3} >
            <div style={{ fontSize: '1.13rem', lineHeight: '20px', fontWeight: 500 }}>
              <Typography sx={{
                fontSize: '18px',
                fontWeight: '500',
                lineHeight: '20px',
              }}>
                {displayTitle}
                {displayTitleLine2 && <><br/>{displayTitleLine2}</>}
              </Typography>
              {displaySubtitle ? <Typography mb={'-5px'} fontSize="small" fontWeight={300}>{displaySubtitle}</Typography> : ''}
            </div>
          </Box>
          { order && isAdhocPortalOrder(order) && <ClosingHeaderActions
            setClosingDetailsDialogOpen={setClosingDetailsDialogOpen}
            addToCalendarOpen={addToCalendarOpen}
            setAddToCalendarOpen={setAddToCalendarOpen}
          /> }
        </Box>
        <Box sx={{ height: '100%', backgroundColor: 'primary.main' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            height='100%'
            sx={{
              p: 2,
              borderLeft: '1px solid #CECECE',
              minWidth: 0,
              backgroundColor: '#F3F3F3',
              borderTopRightRadius: '0',
            }}
          >
            { <Outlet/> }
          </Stack>
        </Box>
      </Box>
      { order && isAdhocPortalOrder(order) && <ClosingDetailsDialog
        open={closingDetailsDialogOpen}
        setClosingDetailsDialogOpen={setClosingDetailsDialogOpen}
        setAddToCalendarOpen={setAddToCalendarOpen}
      /> }
    </>
  )

  return isSmall
    ? onlySection ? mobileSectionOnly : mobile
    : desktop
  ;
}


export function BackButton() {
  const navigateWithSearchParams = useNavigateWithSearchParams();
  const navigateDirect = useNavigate();
  const { pathname } = useLocation()
  const goBack = () => {
    const pathnameParts = pathname.split('/')
    const parentPathname = pathnameParts.slice(0, -1).join('/')
    if (pathnameParts.length === 2) {
      navigateDirect('/')
    } else {      
      navigateWithSearchParams(parentPathname)
    }
  }

  return (
    <Button
      startIcon={<ChevronLeft />}
      onClick={goBack}

      sx={{
        width: 40,
        minWidth: 40,
        height: 41,
        background: 'white',
        ml: '-16px',
        mt: { xs: '-4px', sm: '18px' },
        borderRadius: '0px 10px 10px 0px',
        '&:hover': { backgroundColor: 'primary.light', color: 'primary.contrastText' },
        '.MuiButton-startIcon': { marginRight: '0px'},
      }}
    />
  )
}
